import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'miweather_favorites';

// Default favorites for new users
const DEFAULT_FAVORITES = ['detroit', 'grand-rapids', 'traverse-city', 'marquette'];

interface UseFavoritesResult {
  favorites: string[];
  isFavorite: (locationId: string) => boolean;
  addFavorite: (locationId: string) => void;
  removeFavorite: (locationId: string) => void;
  toggleFavorite: (locationId: string) => void;
}

function getStoredFavorites(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to read favorites from localStorage:', error);
  }
  return DEFAULT_FAVORITES;
}

function storeFavorites(favorites: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
  } catch (error) {
    console.warn('Failed to save favorites to localStorage:', error);
  }
}

export function useFavorites(): UseFavoritesResult {
  const [favorites, setFavorites] = useState<string[]>(() => getStoredFavorites());

  // Sync to localStorage when favorites change
  useEffect(() => {
    storeFavorites(favorites);
  }, [favorites]);

  const isFavorite = useCallback(
    (locationId: string) => favorites.includes(locationId),
    [favorites]
  );

  const addFavorite = useCallback((locationId: string) => {
    setFavorites((prev) => {
      if (prev.includes(locationId)) return prev;
      return [...prev, locationId];
    });
  }, []);

  const removeFavorite = useCallback((locationId: string) => {
    setFavorites((prev) => prev.filter((id) => id !== locationId));
  }, []);

  const toggleFavorite = useCallback((locationId: string) => {
    setFavorites((prev) => {
      if (prev.includes(locationId)) {
        return prev.filter((id) => id !== locationId);
      }
      return [...prev, locationId];
    });
  }, []);

  return {
    favorites,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
  };
}
